import type { Reducer } from 'redux';
import type { DealTemplatesState } from '@makemydeal/dr-dash-types';
import { initActionTypes, bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import {
    DELETE_DEAL_TEMPLATE_FAILURE,
    DELETE_DEAL_TEMPLATE_REQUEST,
    DELETE_DEAL_TEMPLATE_SUCCESS,
    DROP_DELETE_DEAL_TEMPLATE,
    DROP_UPDATE_DEAL_TEMPLATE,
    GET_DEAL_TEMPLATES_FAILURE,
    GET_DEAL_TEMPLATES_REQUEST,
    GET_DEAL_TEMPLATES_SUCCESS,
    GET_MANUAL_DEAL_TEMPLATES_FAILURE,
    GET_MANUAL_DEAL_TEMPLATES_REQUEST,
    GET_MANUAL_DEAL_TEMPLATES_SUCCESS,
    SAVE_DEAL_TEMPLATE_FAILURE,
    SAVE_DEAL_TEMPLATE_REQUEST,
    SAVE_DEAL_TEMPLATE_SUCCESS,
    TOGGLE_SAVE_PAYMENT_TERMS,
    UPDATE_DEAL_TEMPLATE_REQUEST,
    UPDATE_DEAL_TEMPLATE_FAILURE,
    UPDATE_DEAL_TEMPLATE_SUCCESS,
    TRIGGER_TEMPLATE_BUILDER_MODE,
    MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST,
    APPLY_ORIGINAL_TEMPLATE_PRODUCTS,
    APPLY_TEMPLATE_PRODUCTS,
    INIT_TEMPLATE_FROM_DEAL_FAILURE
} from '../actionTypes/dealTemplateActionTypes';
import { OFFER_SAVE_SUCCESS, OFFER_SAVE_FAILURE } from '../actionTypes/offerActionTypes';
import { TEMPLATE_SCENARIO_FETCH_FAILURE } from '../actionTypes/multiScenarioActionTypes';
export type DealTemplateReducer = Reducer<DealTemplatesState>;

const { BOOTSTRAP_FAILURE } = bootstrapActionTypes;
const { INIT_PENCIL_SUCCESS } = initActionTypes;
export const initialState: DealTemplatesState = {
    draft: {
        id: '',
        name: '',
        overwritten: false,
        isError: false,
        isSaving: false,
        isSaved: false
    },
    applied: {
        id: '',
        name: '',
        scenariosCount: 0
    },
    applying: {
        products: [],
        isOriginalTemplateProductsApplied: false
    },
    list: {
        value: [],
        isLoading: false,
        isError: false
    },
    manualList: {
        value: [],
        isLoading: false,
        isError: false
    },
    removing: {
        id: '',
        name: '',
        isError: false,
        isRemoving: false,
        isRemoved: false
    },
    updating: {
        id: '',
        name: '',
        isError: false,
        isUpdating: false,
        isUpdated: false
    },
    savePaymentTerms: false,
    templateBuilderMode: {
        show: false,
        builderModeTab: 'save-template'
    },
    manuallyApplying: {
        id: '',
        name: '',
        isError: false,
        isApplying: false,
        isApplied: false
    }
};

export const reducer: DealTemplateReducer = (state = initialState, action: any) => {
    switch (action.type) {
        case INIT_PENCIL_SUCCESS: {
            const { template } = action.payload;
            return {
                ...state,
                ...(template && {
                    applied: {
                        id: template.id,
                        name: template.name,
                        scenariosCount: template.scenariosCount
                    }
                }),
                ...(template?.products && {
                    applying: {
                        ...state.applying,
                        products: template.products,
                        isOriginalTemplateProductsApplied: false
                    }
                }),
                manuallyApplying: {
                    ...state.manuallyApplying,
                    isApplying: false,
                    isApplied: template ? state.manuallyApplying.isApplying : false
                }
            };
        }
        case OFFER_SAVE_SUCCESS:
            return {
                ...state,
                applied: {
                    id: '',
                    name: '',
                    scenariosCount: 0
                }
            };
        case BOOTSTRAP_FAILURE:
            return {
                ...state,
                manuallyApplying: {
                    ...state.manuallyApplying,
                    isApplied: false,
                    isApplying: false,
                    isError: true
                }
            };
        case OFFER_SAVE_FAILURE:
            return {
                ...state,
                manuallyApplying: {
                    ...state.manuallyApplying,
                    isApplied: false,
                    isApplying: false,
                    isError: true
                }
            };
        case TEMPLATE_SCENARIO_FETCH_FAILURE:
            return {
                ...state,
                manuallyApplying: {
                    ...state.manuallyApplying,
                    isApplied: false,
                    isApplying: false,
                    isError: true
                }
            };
        case INIT_TEMPLATE_FROM_DEAL_FAILURE:
            return {
                ...state,
                manuallyApplying: {
                    ...state.manuallyApplying,
                    isApplied: false,
                    isApplying: false,
                    isError: true
                }
            };
        case SAVE_DEAL_TEMPLATE_SUCCESS: {
            const inventoryType = action.payload.inventoryTypes?.[0] || 'All';
            return {
                ...state,
                draft: {
                    id: action.payload.id,
                    name: action.payload.name,
                    overwritten: action.payload.overwritten,
                    inventoryType,
                    make: action.payload.make,
                    isError: false,
                    isSaving: false,
                    isSaved: true,
                    errorDetails: undefined
                }
            };
        }
        case SAVE_DEAL_TEMPLATE_REQUEST:
            return {
                ...state,
                draft: {
                    ...initialState.draft,
                    isSaving: true
                }
            };
        case SAVE_DEAL_TEMPLATE_FAILURE:
            return {
                ...state,
                draft: {
                    ...initialState.draft,
                    isSaving: false,
                    isError: true,
                    errorDetails: {
                        name: action.payload?.response?.data?.name || 'ApiError',
                        message: action.payload?.response?.data?.message || 'Failed to save template'
                    }
                }
            };
        case GET_DEAL_TEMPLATES_REQUEST:
            return {
                ...state,
                list: {
                    ...state.list,
                    isLoading: true,
                    isError: false
                }
            };
        case GET_DEAL_TEMPLATES_SUCCESS:
            return {
                ...state,
                list: {
                    value: action.payload,
                    isLoading: false,
                    isError: false
                }
            };
        case GET_DEAL_TEMPLATES_FAILURE:
            return {
                ...state,
                list: {
                    value: [],
                    isLoading: false,
                    isError: true
                }
            };
        case GET_MANUAL_DEAL_TEMPLATES_REQUEST:
            return {
                ...state,
                manualList: {
                    ...state.manualList,
                    isLoading: true,
                    isError: false
                }
            };
        case GET_MANUAL_DEAL_TEMPLATES_SUCCESS:
            return {
                ...state,
                manualList: {
                    value: action.payload,
                    isLoading: false,
                    isError: false
                }
            };
        case GET_MANUAL_DEAL_TEMPLATES_FAILURE:
            return {
                ...state,
                manualList: {
                    value: [],
                    isLoading: false,
                    isError: true
                }
            };
        case UPDATE_DEAL_TEMPLATE_REQUEST: {
            const { id, name, inventoryType } = action.meta;
            return {
                ...state,
                updating: {
                    ...initialState.updating,
                    id,
                    name,
                    inventoryType,
                    isUpdating: true,
                    isUpdated: false,
                    isError: false,
                    errorDetails: undefined
                }
            };
        }
        case UPDATE_DEAL_TEMPLATE_SUCCESS: {
            return {
                ...state,
                updating: {
                    ...state.updating,
                    isUpdating: false,
                    isError: false,
                    isUpdated: true
                }
            };
        }
        case UPDATE_DEAL_TEMPLATE_FAILURE: {
            return {
                ...state,
                updating: {
                    ...state.updating,
                    isError: true,
                    isUpdating: false,
                    isUpdated: false,
                    errorDetails: {
                        name: action.payload?.response?.name || 'ApiError',
                        message: action.payload?.response?.message || 'Failed to save template',
                        errors: action.payload?.response?.errors || []
                    }
                }
            };
        }
        case DELETE_DEAL_TEMPLATE_REQUEST: {
            const { id, name, inventoryType } = action.meta.template;
            return {
                ...state,
                removing: {
                    ...initialState.removing,
                    id,
                    name,
                    inventoryType,
                    isRemoving: true,
                    isRemoved: false,
                    isError: false
                }
            };
        }
        case DELETE_DEAL_TEMPLATE_SUCCESS:
            return {
                ...state,
                removing: {
                    ...state.removing,
                    isRemoving: false,
                    isError: false,
                    isRemoved: true
                }
            };
        case DELETE_DEAL_TEMPLATE_FAILURE:
            return {
                ...state,
                removing: {
                    ...state.removing,
                    isError: true,
                    isRemoving: false,
                    isRemoved: false
                }
            };
        case DROP_DELETE_DEAL_TEMPLATE:
            return {
                ...state,
                removing: {
                    ...initialState.removing
                }
            };
        case DROP_UPDATE_DEAL_TEMPLATE:
            return {
                ...state,
                updating: {
                    ...state.updating,
                    isUpdating: false,
                    isUpdated: false,
                    isError: false,
                    errorDetails: undefined
                }
            };
        case TOGGLE_SAVE_PAYMENT_TERMS:
            return {
                ...state,
                savePaymentTerms: action.payload.savePaymentTerms
            };
        case TRIGGER_TEMPLATE_BUILDER_MODE:
            return {
                ...state,
                templateBuilderMode: action.payload
            };
        case MANUALLY_APPLY_DEAL_TEMPLATE_REQUEST:
            return {
                ...state,
                manuallyApplying: {
                    id: action.payload.templateId,
                    name: action.payload.templateName,
                    isError: false,
                    isApplied: false,
                    isApplying: true,
                    ...(action.payload.source && { source: action.payload.source })
                }
            };
        case APPLY_TEMPLATE_PRODUCTS: {
            return {
                ...state,
                applying: {
                    ...state.applying,
                    products: []
                }
            };
        }
        case APPLY_ORIGINAL_TEMPLATE_PRODUCTS: {
            return {
                ...state,
                applying: {
                    products: [],
                    isOriginalTemplateProductsApplied: true
                }
            };
        }

        default:
            return state;
    }
};
